import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { UIController } from 'app/views/ui/UIController';
import { SectionType } from 'app/data/Enums';
import { Events } from 'core/events/Events';

import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { LoaderGraphic } from 'app/module/global/LoaderGraphic';
import { AppEvents } from 'app/events/AppEvents';

import { ViewReadyEvent } from 'app/events/ViewReadyEvent';
import DelayHelper from 'utils/timers';
import { Simple } from 'utils/analytics';

import { PreloadView } from './PreloadView';
import { PreloadPlan } from './PreloadPlan';

/**
 * Boot-time loader controller (registered as 'Preload' on .preload, and
 * exposed as globals.preloadController for HomeTimeline). Runs once at
 * boot. Orchestrates the per-concern sub-objects in this folder (see
 * preload.types.ts for the decomposition pattern, established in
 * src/app/views/work/). Keeps: the lifecycle hooks, the full public surface
 * used by AppController / HomeController / HomeTimeline, every
 * Events.on(...) registration, the boot-timing dispatch points
 * (INTIAL_PRELOAD_COMPLETE / DEEP_LINK_LOADER_COMPLETE / the deeplink
 * ViewReadyEvent), the run-once _doPreloader flow and all cross-controller
 * access (the injected UIController).
 */
export class PreloadController extends Controller {
	public groupLoadDelay: number = 20; //Set to larger throttle to simulate slow connections
	public onFullyLoaded: CallableFunction;
	//NOTE: unused public hook moved intact — never assigned by any caller
	public onHomePixelDataReady: CallableFunction;
	public isFullyLoaded: boolean = false;

	private _openingPreloaderCreated: boolean = false;
	private _openingPreloadComplete: boolean = false;
	private _openingMinTimePassed: boolean = true;

	private _section: SectionType = SectionType.HOME;
	private _subsection: string;

	private _historyStateTimeoutId: number;

	private _uiController: UIController;

	private _isFirefox: boolean = false;

	//Per-concern sub-objects (see preload.types.ts for the pattern):
	private _view: PreloadView;
	private _plan: PreloadPlan;

	constructor(id: string) {
		super(id);

		this._view = new PreloadView(this.DOM, {
			getSection: () => this._section,
			showUI: () => this._uiController.show()
		});

		this._plan = new PreloadPlan({
			getSection: () => this._section,
			getSubsection: () => this._subsection,
			getGroupLoadDelay: () => this.groupLoadDelay,
			onGroupProgress: (ratio: number) => {
				if (this._view.loaderGraphic) {
					this._view.loaderGraphic.progress(ratio);
				}
			}
		});
	}

	public setControllers(ui: UIController): void {
		this._uiController = ui;
		globals.preloadController = this;

		Events.on('click', document.querySelector('.go-fullscreen'), () => {
			const $body = document.querySelector('body');
			this.DOM.addClass($body, '-fullscreen');

			if (document.documentElement.requestFullscreen) {
				document.documentElement.requestFullscreen();
			} else if (document.documentElement.msRequestFullscreen) {
				document.documentElement.msRequestFullscreen();
			} else if (document.documentElement.mozRequestFullScreen) {
				document.documentElement.mozRequestFullScreen();
			} else if (document.documentElement.webkitRequestFullscreen) {
				document.documentElement.webkitRequestFullscreen();
			}

			Simple.event('click_fullscreen_button');
		});
	}

	/** Set intial state: */
	public setIntialState(state: HistoryStateModel): void {
		this._view.setupOpeningMessages();

		this._isFirefox = false;
		this._section = state.section;
		this._subsection = state.subsection;
	}

	/** Show the view */
	public show(): void {
		this._view.show();
	}

	/** Loading is completed so hide: */
	public hide(): void {
		this._view.hide();
	}

	/**
	 * Particles can take a while to add, so factor them into loading:
	 */
	public homeParticlesCreated() {
		this._view.homeParticlesCreated();
	}

	/**
	 * Particles can take a while to add, so factor them into loading:
	 */
	public homePixelDataReady() {
		this._view.homePixelDataReady();
		if (this.onHomePixelDataReady) {
			this.onHomePixelDataReady();
		}
	}

	public hideForIntro() {
		this._view.hideForIntro();
	}

	public DOMReady(): void {
		this._view.setContainer(this.container);

		//Section is set as soon as the app via URL routing:
		this.DOM.removeClass(this.container, '-notready');

		if (!this._openingPreloaderCreated && !this._isFirefox) {
			this._doPreloader();
		} else {
			this._showFirefoxDialog();
		}
	}

	//NOTE: effectively-dead method kept in the controller (NOT moved to
	//PreloadView) because it embeds an Events.on registration and gates the
	//_doPreloader boot flow. Unreachable in practice: _isFirefox is hard-set
	//to false in setIntialState and DOMReady only runs once.
	private _showFirefoxDialog() {
		let $popup = document.querySelector<HTMLElement>('#firefox-dialog');

		$popup.style.display = 'block';

		this.DOM.setHTML(
			$popup.querySelector('.firefox__copy'),
			'Looks like your using <strong>Firefox</strong>. At DEBUG we love its approach to security <br/>&amp; privacy, but it can be slow at running full screen animation smoothly. <br/>For the best experience on this site please use <strong>Chrome</strong>, <strong>Edge</strong> or <strong>Safari</strong>'
		);

		this.DOM.setHTML($popup.querySelector('.firefox__button'), 'Continue');

		gsap.TweenMax.fromTo(
			$popup.querySelector('.firefox__image'),
			0.5,
			{ opacity: 0, y: 20 },
			{ opacity: 1, y: 0, delay: 0.5 }
		);
		gsap.TweenMax.fromTo(
			$popup.querySelector('.firefox__copy'),
			0.5,
			{ opacity: 0, y: 20 },
			{ opacity: 1, y: 0, delay: 0.8 }
		);
		gsap.TweenMax.fromTo(
			$popup.querySelector('.firefox__button'),
			0.5,
			{ opacity: 0, y: 20 },
			{ opacity: 1, y: 0, delay: 1.2 }
		);

		Events.on('click', $popup.querySelector('.firefox__button'), () => {
			this._hideFirefox();
		});

		this.container.style.display = 'none';
	}

	//NOTE: effectively-dead method kept in the controller — only reachable
	//from _showFirefoxDialog (see note there)
	private _hideFirefox() {
		let $popup = document.querySelector<HTMLElement>('#firefox-dialog');

		gsap.TweenMax.to($popup.querySelector('.firefox__image'), 0.5, { opacity: 0, y: 20, delay: 0.2 });
		gsap.TweenMax.to($popup.querySelector('.firefox__copy'), 0.5, { opacity: 0, y: 20, delay: 0.1 });
		gsap.TweenMax.to($popup.querySelector('.firefox__button'), 0.5, { opacity: 0, y: 20, delay: 0 });

		DelayHelper.setTimeout(() => {
			$popup.style.display = 'none';
			this._doPreloader();
		}, 500);
	}

	private _doPreloader() {
		this._view.markReady();

		DelayHelper.setTimeout(() => {
			this._view.preloaderText();
		}, 520);

		DelayHelper.setTimeout(() => {
			this._plan.setPreloader();
		}, 1000);

		this._createOpeningTimer();
		this._openingPreloaderCreated = true;
	}

	public modulesMounted(): void {
		if (!this._view.loaderGraphic) {
			this._view.loaderGraphic = this.module.get('preload-graphic') as LoaderGraphic;
			this._view.loaderGraphic.onLoaderGraphicComplete = this.fullLoadingComplete.bind(this);
			if (this._section === SectionType.WORK) {
				this._view.loaderGraphic.setLoadPercentages(1, 0, 1000);
			} else if (this._section == SectionType.PROJECT) {
				this._view.loaderGraphic.setLoadPercentages(1, 0, 300);
			}
		}
	}

	public fullLoadingComplete() {
		this.isFullyLoaded = true;
		if (this.onFullyLoaded) {
			this.onFullyLoaded();
		}
	}

	//Set a loaded HTML template:
	public setHTML(section: string, element: Element): void {
		this._plan.setHTML(section, element);
	}

	/** Show a quick loader to cover over history state changes. When complete triggers an event that is picked up in AppController */
	public showDeepLinkLoader(activeController: Controller): void {
		this.show();

		//Test
		clearTimeout(this._historyStateTimeoutId);
		this._historyStateTimeoutId = setInterval(() => {
			if ((activeController as any).readyForDeeplink()) {
				clearTimeout(this._historyStateTimeoutId);
				Events.dispatch(AppEvents.DEEP_LINK_LOADER_COMPLETE);
				this.hide();
			}
		}, 20);
	}

	/** View is loaded, setup and ready to show */
	public viewReady(event: ViewReadyEvent): boolean {
		if (this._plan.coreLoadComplete) {
			return true;
		}

		return false;
	}

	//Opening-complete join (boot-timing dispatch points stay here)
	//-------------------------------------------------------------------------

	/** Create an opening timer so intial preload does get caught in cache loading, which will look a bit rough an ready on */
	private _createOpeningTimer(): void {
		DelayHelper.setTimeout(() => {
			this._openingMinTimePassed = true;
			this._testOpeningComplete();
		}, 2000);
	}

	/** Test if the opening is 10% complete (timer and content loading) */
	private _testOpeningComplete(): void {
		if (!this._openingMinTimePassed || !this._openingPreloadComplete) {
			return;
		}

		//Hide this
		//Flag loaded event
		//Delay to allow the pixi loading to complete

		DelayHelper.setTimeout(() => Events.dispatch(AppEvents.INTIAL_PRELOAD_COMPLETE), 20);
	}

}
